import React, { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import OnTourBar from '../../components/Common/OnTourBar';
import { getOnboardingData } from './services/Teacher.service';
import { TeacherOnboardingStatusEnum } from '../../interfaces/user.interface';

export enum TeacherOnboardingPagesEnum {
  OnboardingVideo = '/teacher/onboarding',
  OnboardingSchoolInfo = '/teacher/onboarding/school-info',
  OnboardingClassInfo = '/teacher/onboarding/class-info',
  OnboardingHowDidYouFindUs = '/teacher/onboarding/how-did-you-find-us',
  OnboardingQuestionary = '/teacher/onboarding/questionary',
  OnboardingShippingAddress = '/teacher/onboarding/shipping-address',
  // OnboardingTimeline = '/teacher/onboarding/timeline',
}

export const TeacherOnboardingPagesSteps = {
  [TeacherOnboardingPagesEnum.OnboardingSchoolInfo]: 1,
  [TeacherOnboardingPagesEnum.OnboardingClassInfo]: 2,
  [TeacherOnboardingPagesEnum.OnboardingHowDidYouFindUs]: 3,
};

export interface TeacherOnboardingProps {
  children: ReactNode;
}

const TeacherOnboarding = ({ children }: TeacherOnboardingProps) => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();

  const user = useSelector((state: RootState) => state.user.user);
  const currentPathname = useSelector((state: RootState) => state.app.currentPathname);
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);

  // Redirect to dashboard if onboarding is already complete
  useEffect(() => {
    if (user?.teacherOnboardingStatus === TeacherOnboardingStatusEnum.Complete) {
      navigateTo('/classroom/dashboard', { replace: true });
    }
  }, [user?.teacherOnboardingStatus, navigateTo]);

  const allSteps = Object.values(TeacherOnboardingPagesSteps).filter((item) => item > 0);
  const [onboardingStep, setOnboardingStep] = useState<number>(-1);
  const [pageVisible, setPageVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    if (teacherOnboarding === null) {
      getOnboardingData(dispatch);
    }
  }, []);

  useEffect(() => {
    setOnboardingStep(TeacherOnboardingPagesSteps[currentPathname as keyof typeof TeacherOnboarding]);

    // Page entrance animation sequence
    setPageVisible(false);
    setContentVisible(false);

    // First animate the page container
    setTimeout(() => {
      setPageVisible(true);

      // Then animate the content
      setTimeout(() => {
        setContentVisible(true);
      }, 300);
    }, 100);
  }, [currentPathname]);

  const onboardingBarInfo: Record<number, { title: string; description: string }> = {
    1: { title: 'School information', description: 'Tell us about your school' },
    2: { title: 'Class information', description: 'Tell us about your role and classes' },
    3: { title: 'Almost done!', description: 'How did you hear about us?' },
  };

  const barInfo = onboardingBarInfo[onboardingStep] || onboardingBarInfo[1];

  return (
    <div className={`App transition-opacity duration-500 ease-in-out ${pageVisible ? 'opacity-100' : 'opacity-0'}`}>
      {onboardingStep > 0 ? (
        <OnTourBar
          stepNumber={onboardingStep}
          totalSteps={allSteps.length}
          title={barInfo.title}
          description={barInfo.description}
          showButton={false}
        />
      ) : (
        <NavigationBar hideActions={true} />
      )}
      <div className="bg-storytan mx-auto">

        <div className="min-h-[calc(100vh-10rem)] flex flex-col font-sofiaSans px-4 sm:px-6 lg:px-8">
          <div
            className={`flex-1 pb-12 md:pb-0 flex items-start justify-center transition-all duration-700 ease-in-out ${contentVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}`}
          >
            <div className="w-full max-w-md pt-4">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherOnboarding;
